import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { Check, CloudOff, LoaderCircle, TriangleAlert, X } from "lucide-react";
import { cn } from "./primitives";

// ---------------------------------------------------------------------------
// Status contract: occupied = navy fill, overstay = danger fill, absent = absent fill,
// free = dashed free-line outline. Always paired with a text label.

export type BoatStatus = "occupied" | "overstay" | "absent" | "free";

export function StatusSwatch({ status, className }: { status: BoatStatus; className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        "inline-block h-3.5 w-3.5 shrink-0 rounded-[3px]",
        status === "occupied" && "bg-occupied",
        status === "overstay" && "bg-danger",
        status === "absent" && "border border-absent-border bg-absent",
        status === "free" && "border-2 border-dashed border-free-line",
        className,
      )}
    />
  );
}

export function Legend({ labels, className }: { labels: Record<BoatStatus, string>; className?: string }) {
  return (
    <ul className={cn("m-0 flex list-none flex-wrap gap-x-5 gap-y-2 p-0 text-13 text-ink", className)}>
      {(["occupied", "overstay", "absent", "free"] as const).map((s) => (
        <li key={s} className="flex items-center gap-1.5">
          <StatusSwatch status={s} />
          {labels[s]}
        </li>
      ))}
    </ul>
  );
}

export type PillTone = "neutral" | "overstay" | "unpaid" | "ok" | "absent" | "accent" | "paid";
const pillTones: Record<PillTone, string> = {
  neutral: "bg-track text-primary",
  paid: "bg-track text-primary",
  overstay: "bg-danger-tint text-danger-text",
  unpaid: "bg-unpaid-tint text-unpaid-text",
  ok: "bg-ok-tint text-ok-text",
  absent: "bg-absent text-absent-text",
  accent: "bg-accent-tint text-accent-text",
};

export function Pill({ tone = "neutral", className, children }: { tone?: PillTone; className?: string; children: React.ReactNode }) {
  return (
    <span className={cn("inline-flex items-center whitespace-nowrap rounded-pill px-2 py-1 text-12 font-bold", pillTones[tone], className)}>
      {children}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Quay bar: boats along the quay drawn to scale

export interface QuayBarSegment {
  key: string;
  metres: number;
  kind: "occupied" | "overstay" | "suggested" | "free" | "track" | "absent";
  label?: string;
  /** Name of the clickable segment for screen readers, when it must not print visible text like `label` does. */
  ariaLabel?: string;
}

export function QuayBar({
  segments,
  height = 22,
  className,
  rounded = "boat",
  onSegmentClick,
  ariaLabel,
}: {
  segments: QuayBarSegment[];
  height?: number;
  className?: string;
  rounded?: "boat" | "pill" | "flat";
  onSegmentClick?: (key: string) => void;
  ariaLabel?: string;
}) {
  const radius = rounded === "pill" ? "rounded-pill" : rounded === "boat" ? "rounded-[3px_3px_10px_10px]" : "rounded-[4px]";
  return (
    <div className={cn("flex gap-0.5", className)} style={{ height }} role={ariaLabel ? (onSegmentClick ? "group" : "img") : undefined} aria-label={ariaLabel} aria-hidden={ariaLabel ? undefined : true}>
      {segments
        .filter((s) => s.metres > 0)
        .map((s) => {
          const style = { flex: `${s.metres} 1 0` };
          const cls = cn(
            "min-w-[2px]",
            s.kind === "free"
              ? "flex items-center justify-center overflow-hidden rounded-tile border-2 border-dashed border-free-line text-13 font-bold whitespace-nowrap text-free-text"
              : s.kind === "track"
                ? "rounded-[4px] bg-track"
                : radius,
            s.kind === "occupied" && "bg-occupied",
            s.kind === "overstay" && "bg-danger",
            s.kind === "suggested" && "rounded-[4px] bg-accent",
            s.kind === "absent" && "border border-absent-border bg-absent",
          );
          if (onSegmentClick)
            return (
              <button key={s.key} type="button" title={s.label} aria-label={s.ariaLabel ?? s.label} className={cls} style={style} onClick={() => onSegmentClick(s.key)}>
                {s.kind === "free" && s.label ? <span className="truncate px-1">{s.label}</span> : null}
              </button>
            );
          return (
            <div key={s.key} title={s.label} className={cls} style={style}>
              {s.kind === "free" && s.label ? <span className="truncate px-1">{s.label}</span> : null}
            </div>
          );
        })}
    </div>
  );
}

/** Thin occupancy bar for side panels: occupied (navy) vs free (track). */
export function OccupancyBar({ occupied, total, overbooked }: { occupied: number; total: number; overbooked?: boolean }) {
  const occ = Math.min(occupied, total);
  return (
    <div className="flex h-2.5 overflow-hidden rounded-[5px] bg-track" aria-hidden="true">
      <div className={overbooked ? "bg-danger" : "bg-occupied"} style={{ flex: `${occ} 1 0` }} />
      <div style={{ flex: `${Math.max(0, total - occ)} 1 0` }} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sync pill (dock header)

export type SyncState = "synced" | "pending" | "offline" | "syncing" | "error";

export function SyncPill({ state, label }: { state: SyncState; label: string }) {
  const tone =
    state === "synced"
      ? "bg-ok-tint text-ok-text"
      : state === "pending" || state === "syncing"
        ? "bg-absent text-absent-text"
        : state === "error"
          ? "bg-danger-tint text-danger-text"
          : "bg-track text-ink";
  const Icon = state === "synced" ? Check : state === "offline" ? CloudOff : state === "error" ? TriangleAlert : LoaderCircle;
  return (
    <span role="status" aria-live="polite" className={cn("inline-flex items-center gap-1.5 rounded-pill px-2.5 py-1 text-13 font-semibold", tone)}>
      <Icon size={12} strokeWidth={3} aria-hidden="true" className={state === "syncing" ? "animate-spin" : undefined} />
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Dock pieces

export function StepHeader({ n, children, htmlFor }: { n: number; children: React.ReactNode; htmlFor?: string }) {
  const Title = htmlFor ? "label" : "span";
  return (
    <div className="flex items-center gap-2.5">
      <span aria-hidden="true" className="flex h-7 w-7 items-center justify-center rounded-pill bg-primary text-15 font-bold text-on-primary">
        {n}
      </span>
      <Title {...(htmlFor ? { htmlFor } : {})} className="text-17 font-bold">
        {children}
      </Title>
    </div>
  );
}

export function Chip({
  pressed,
  className,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { pressed: boolean }) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      className={cn(
        "h-14 rounded-chip border-2 border-primary text-18 font-bold",
        pressed ? "bg-primary text-on-primary" : "bg-surface text-ink",
        className,
      )}
      {...props}
    />
  );
}

// ---------------------------------------------------------------------------
// KPI card

export function Kpi({
  label,
  value,
  tone = "default",
  hint,
}: {
  label: React.ReactNode;
  value: React.ReactNode;
  tone?: "default" | "ok" | "absent" | "danger" | "accent";
  hint?: React.ReactNode;
}) {
  return (
    <div className="flex min-w-0 flex-[1_1_200px] flex-col gap-1 rounded-panel border border-border bg-surface p-4">
      <span className="text-13 text-muted">{label}</span>
      <span
        className={cn(
          "text-30 font-extrabold leading-tight",
          tone === "ok" && "text-ok",
          tone === "absent" && "text-absent-kpi",
          tone === "danger" && "text-danger",
          tone === "accent" && "text-accent-text",
        )}
      >
        {value}
      </span>
      {hint ? <span className="text-13 text-muted">{hint}</span> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dialog (Radix)

export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  closeLabel,
  wide,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: React.ReactNode;
  description?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  closeLabel: string;
  wide?: boolean;
}) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-40 bg-black/40" />
        <DialogPrimitive.Content
          className={cn(
            "fixed left-1/2 top-1/2 z-50 flex max-h-[92vh] w-[calc(100vw-32px)] -translate-x-1/2 -translate-y-1/2 flex-col gap-4 overflow-y-auto rounded-card border border-border bg-surface p-5 text-ink shadow-xl",
            wide ? "max-w-3xl" : "max-w-lg",
          )}
          {...(description ? {} : { "aria-describedby": undefined })}
        >
          <div className="flex items-start justify-between gap-3">
            <DialogPrimitive.Title className="m-0 text-20 font-extrabold">{title}</DialogPrimitive.Title>
            <DialogPrimitive.Close aria-label={closeLabel} className="-m-1 rounded-btn p-1 text-muted hover:bg-track">
              <X size={20} aria-hidden="true" />
            </DialogPrimitive.Close>
          </div>
          {description ? <DialogPrimitive.Description className="m-0 text-14 text-muted">{description}</DialogPrimitive.Description> : null}
          {children}
          {footer ? <div className="flex flex-wrap justify-end gap-2">{footer}</div> : null}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

// ---------------------------------------------------------------------------
// Tabs (Radix)

export function Tabs({
  value,
  onValueChange,
  tabs,
  label,
  children,
}: {
  value: string;
  onValueChange: (v: string) => void;
  tabs: Array<{ value: string; label: React.ReactNode }>;
  label: string;
  children: React.ReactNode;
}) {
  return (
    // manual: a tab opens on a click or Enter, not on focus. With automatic activation a dialog that closes and hands
    // focus back to a tab it interrupted (the unsaved-changes question) opens that tab again, and so asks again, for ever.
    <TabsPrimitive.Root activationMode="manual" value={value} onValueChange={onValueChange} className="flex flex-col gap-4">
      <TabsPrimitive.List aria-label={label} className="flex flex-wrap gap-1 border-b border-border">
        {tabs.map((t) => (
          <TabsPrimitive.Trigger
            key={t.value}
            value={t.value}
            className="-mb-px border-b-2 border-transparent px-3 py-2 text-14 font-semibold text-muted data-[state=active]:border-primary data-[state=active]:text-ink"
          >
            {t.label}
          </TabsPrimitive.Trigger>
        ))}
      </TabsPrimitive.List>
      {children}
    </TabsPrimitive.Root>
  );
}
export const TabPanel = TabsPrimitive.Content;

// ---------------------------------------------------------------------------
// Toast region (aria-live)

export interface ToastMessage {
  id: number;
  text: string;
  tone?: "ok" | "error";
}

export function ToastRegion({ toasts }: { toasts: ToastMessage[] }) {
  return (
    <div aria-live="polite" className="pointer-events-none fixed inset-x-0 bottom-4 z-[60] flex flex-col items-center gap-2 px-4">
      {toasts.map((t) => (
        <div
          key={t.id}
          role={t.tone === "error" ? "alert" : "status"}
          className={cn(
            "pointer-events-auto rounded-panel px-4 py-3 text-15 font-semibold shadow-lg",
            t.tone === "error" ? "bg-danger text-white" : "bg-bar text-on-bar",
          )}
        >
          {t.text}
        </div>
      ))}
    </div>
  );
}
